import React from 'react';
import { Link } from 'react-router-dom';
import { User } from 'lucide-react';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || '/media';

const admins = [
  { name:'Alaran Ibrahim', role:'Data & Chief Administrator', image:'admin_me.jpeg', color:'from-emerald-50 to-blue-50 border-emerald-200', badge:'text-emerald-700', responsibilities:['Strategic planning','Community engagement','Quality assurance','Data entry oversight','Database maintenance'] },
  { name:'Adeyemi Favor', role:'Content Curator', image:'admin_adeyemi.jpeg', color:'from-blue-50 to-purple-50 border-blue-200', badge:'text-blue-700', responsibilities:['Content review','Historical validation','Documentation'] },
  { name:'Adegunju Tobi John', role:'Technical Lead', image:'admin_adegunju.jpeg', color:'from-purple-50 to-pink-50 border-purple-200', badge:'text-purple-700', responsibilities:['Platform development','Technical support','Data management'] },
  { name:'Odekunle Peter', role:'Media Coordinator', image:'admin_odekunle.jpeg', color:'from-rose-50 to-red-50 border-rose-200', badge:'text-rose-700', responsibilities:['Photo/video archiving','Visual design','Media processing'] },
];

const CreditsPage = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <div className="flex items-center gap-3 mb-8">
      <span className="text-5xl">👥</span>
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Admin & Credits</h1>
        <p className="text-gray-600">Meet the team behind Gbongan Archive</p>
      </div>
    </div>

    {/* About */}
    <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-xl mb-10">
      <h3 className="text-xl font-semibold mb-3 text-gray-900">About This Archive</h3>
      <p className="text-gray-700 mb-3 leading-relaxed">
        The Gbongan Archive is a comprehensive digital platform dedicated to preserving and celebrating the rich history, culture, and achievements of the Gbongan community. This platform serves as a bridge connecting our glorious past with our promising future.
      </p>
      <p className="text-gray-700 leading-relaxed">
        Through collaborative efforts of community members, historians, and technology enthusiasts, we have created a living archive that grows with each contribution, ensuring that our heritage remains accessible to current and future generations.
      </p>
    </div>

    {/* Admin Team */}
    <h2 className="text-2xl font-bold mb-6 text-gray-900">Administrative Team</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {admins.map(admin => (
        <div key={admin.name} className={`bg-gradient-to-br ${admin.color} border rounded-2xl p-6 hover:shadow-lg transition`}>
          <div className="flex flex-col items-center text-center">
            <div className="w-24 h-24 rounded-full overflow-hidden mb-4 shadow-md bg-gray-200 flex items-center justify-center">
              <img src={`${MEDIA_URL}/images/${admin.image}`} alt={admin.name} className="w-full h-full object-cover"
                onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
              <User className="w-12 h-12 text-gray-400 hidden" />
            </div>
            <h4 className="font-bold text-lg text-gray-900 mb-1">{admin.name}</h4>
            <p className={`text-sm font-semibold mb-3 ${admin.badge}`}>{admin.role}</p>
            <div className="w-full bg-white rounded-xl p-3 text-xs text-gray-700 text-left">
              <p className="font-semibold mb-2">Responsibilities:</p>
              <ul className="space-y-1">
                {admin.responsibilities.map(r => <li key={r} className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />{r}</li>)}
              </ul>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Special Thanks */}
    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-2xl p-8 mb-8">
      <h3 className="text-xl font-semibold mb-4 text-gray-900">Special Thanks</h3>
      <p className="text-gray-700 mb-5 leading-relaxed">This archive would not be possible without the support and contributions of countless community members, elders, and supporters who have shared their knowledge, photos, stories, and expertise.</p>
      <div className="space-y-3">
        {[
          ['Gbongan Culture, Heritage & History Admin and crew', 'Prince Adelani Ajanaku, Dapo Adediran, and others for their invaluable records and data contributions to the archive'],
          ['Gbongan Olufi Day 2025 Media & Publicity crew', 'For capturing and providing the amazing and iconic pictures that bring our heritage to life'],
          ['Pastor Okiki Jesu', 'For composing and contributing the beautiful Olufi anthem that celebrates our community'],
          ['Mr Femi Adesina', 'For providing the insightful write-up on the history of Gbongan that serves as a foundational piece for our archive'],
        ].map(([title, desc]) => (
          <div key={title} className="bg-white rounded-xl p-4 border border-emerald-100">
            <p className="font-semibold text-emerald-800 mb-1">{title}</p>
            <p className="text-gray-700 text-sm">{desc}</p>
          </div>
        ))}
      </div>
    </div>

    {/* CTA */}
    <div className="bg-emerald-700 text-white rounded-2xl p-8 text-center">
      <h3 className="text-xl font-semibold mb-2">Get Involved</h3>
      <p className="mb-5 text-emerald-100">We welcome contributions from all community members. Share your stories, photos, and knowledge to help preserve our heritage.</p>
      <div className="flex gap-3 justify-center flex-wrap">
        <Link to="/contribute/contact" className="bg-white text-emerald-700 px-6 py-2.5 rounded-xl font-semibold hover:bg-emerald-50 transition">Contact Us</Link>
        <Link to="/chatroom" className="border-2 border-white text-white px-6 py-2.5 rounded-xl font-semibold hover:bg-emerald-600 transition">Join Chatroom</Link>
      </div>
    </div>
  </div>
);

export default CreditsPage;
